import { useState } from 'react'
import { Modal, Form, Button, Alert } from 'react-bootstrap'
import { useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'

const CarteraBulkUploadModal = ({ show, onHide }) => {
  const queryClient = useQueryClient()
  const [bulkResult, setBulkResult] = useState(null)

  const downloadTemplate = async (formato) => {
    try {
      const response = await api.get(
        `/clients/clients/descargar-plantilla-cartera/?formato=${formato}`,
        { responseType: 'blob' }
      )
      const url = window.URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `plantilla_cartera_clientes.${formato}`
      a.click()
      window.URL.revokeObjectURL(url)
      toast.success(`Plantilla ${formato.toUpperCase()} descargada`)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al descargar plantilla')
    }
  }

  const bulkUploadMutation = useMutation(
    async (file) => {
      const formData = new FormData()
      formData.append('file', file)
      const response = await api.post('/clients/clients/carga-masiva/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
    {
      onSuccess: (data) => {
        setBulkResult(data)
        queryClient.invalidateQueries('clients')
        if (data.truncated) {
          toast.warning(
            `El archivo supera el límite de ${data.max_rows} filas. Solo se procesaron las primeras ${data.max_rows}.`
          )
        }
        if (data.loaded_count > 0) {
          toast.success(`${data.loaded_count} cliente(s) creado(s) correctamente`)
        }
        if ((data.errors?.length || 0) > 0 && (data.loaded_count || 0) === 0) {
          toast.warning('Carga completada con errores. Revise el detalle.')
        }
      },
      onError: (error) => {
        const msg = error.response?.data?.error || error.message || 'Error en la carga'
        setBulkResult({ error: msg })
      },
    }
  )

  const handleClose = () => {
    setBulkResult(null)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Carga masiva de clientes</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Sube un archivo CSV, XML o Excel con los clientes a dar de alta. Formatos: .csv (UTF-8), .xml, .xlsx.
          Máximo 1000 registros por archivo. Solo altas; RFC duplicados se omiten.
        </p>
        <div className="mb-3">
          <Form.Label>Descargar plantilla</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('csv')}>
              Descargar CSV
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('xml')}>
              Descargar XML
            </Button>
            <Button variant="outline-secondary" size="sm" onClick={() => downloadTemplate('xlsx')}>
              Descargar Excel
            </Button>
          </div>
        </div>
        <Form
          onSubmit={(e) => {
            e.preventDefault()
            const fileInput = e.target.querySelector('input[type="file"]')
            const file = fileInput?.files?.[0]
            if (!file) {
              toast.error('Selecciona un archivo')
              return
            }
            setBulkResult(null)
            bulkUploadMutation.mutate(file)
          }}
        >
          <Form.Group className="mb-3">
            <Form.Label>Archivo</Form.Label>
            <Form.Control type="file" accept=".csv,.xml,.xlsx" required />
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Cerrar
            </Button>
            <Button variant="primary" type="submit" disabled={bulkUploadMutation.isLoading}>
              {bulkUploadMutation.isLoading ? 'Cargando...' : 'Cargar'}
            </Button>
          </div>
        </Form>

        {bulkResult && !bulkResult.error && (
          <Alert variant={bulkResult.errors?.length > 0 ? 'warning' : 'success'} className="mt-3">
            <strong>Resultado:</strong>
            <ul className="mb-0 mt-2">
              <li>Creados: {bulkResult.loaded_count ?? 0}</li>
              <li>Omitidos (RFC duplicado): {bulkResult.skipped_count ?? 0}</li>
              {(bulkResult.errors?.length ?? 0) > 0 && (
                <li>Errores de validación: {bulkResult.errors.length}</li>
              )}
              {bulkResult.truncated && (
                <li>
                  Archivo truncado: se procesaron {bulkResult.max_rows} de{' '}
                  {bulkResult.total_rows_in_file} filas
                </li>
              )}
            </ul>
            {bulkResult.errors?.length > 0 && (
              <div className="mt-2">
                <strong>Detalle de errores:</strong>
                <ul className="small mb-0 mt-1" style={{ maxHeight: 150, overflowY: 'auto' }}>
                  {bulkResult.errors.map((err, idx) => (
                    <li key={idx}>
                      Fila {err.fila}: {err.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>
        )}
        {bulkResult?.error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {bulkResult.error}
          </Alert>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default CarteraBulkUploadModal
